import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  downloadReconciliationReportCsv,
  fetchReconciliationReport,
  type ReconciliationReportParams,
  type ReconciliationReportResponse,
} from '../api/reports';
import {
  collapseEmptyHistory,
  reconciliationTrendHasActivity,
} from '../components/reports/collapseEmptyHistory';
import { ReportGeneratedHeader } from '../components/reports/ReportGeneratedHeader';
import { ShowEmptyDaysToggle } from '../components/reports/ShowEmptyDaysToggle';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant } from '../utils/datetime';

const discrepancyOptions = [
  { value: '', label: 'All discrepancies' },
  { value: 'missing_listing', label: 'Missing listing' },
  { value: 'quantity_mismatch', label: 'Quantity mismatch' },
  { value: 'price_mismatch', label: 'Price mismatch' },
  { value: 'unknown', label: 'Unknown' },
] as const;

export const ReportsReconciliationPage: React.FC = () => {
  const [report, setReport] = useState<ReconciliationReportResponse | null>(null);
  const [view, setView] = useState<'daily' | 'weekly'>('daily');
  const [channel, setChannel] = useState('');
  const [setName, setSetName] = useState('');
  const [condition, setCondition] = useState('');
  const [language, setLanguage] = useState('');
  const [discrepancyType, setDiscrepancyType] = useState('');
  const [limit, setLimit] = useState('25');
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmptyDays, setShowEmptyDays] = useState(false);

  const buildParams = useCallback((): ReconciliationReportParams => {
    const parsedLimit = Number.parseInt(limit, 10);
    return {
      view,
      channel: channel.trim() || undefined,
      set_name: setName.trim() || undefined,
      condition: condition.trim() || undefined,
      language: language.trim() || undefined,
      discrepancy_type: discrepancyType.trim() ? (discrepancyType as ReconciliationReportParams['discrepancy_type']) : undefined,
      limit: Number.isNaN(parsedLimit) || parsedLimit <= 0 ? 25 : parsedLimit,
    };
  }, [channel, condition, discrepancyType, language, limit, setName, view]);

  const loadReport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const nextReport = await fetchReconciliationReport(buildParams());
      setReport(nextReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reconciliation report');
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    void loadReport();
  }, []);

  const handleApplyFilters = async (event: React.FormEvent) => {
    event.preventDefault();
    await loadReport();
  };

  const handleExportCsv = async () => {
    try {
      setIsExporting(true);
      await downloadReconciliationReportCsv(buildParams());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export reconciliation report');
    } finally {
      setIsExporting(false);
    }
  };

  const trendRows = report
    ? collapseEmptyHistory(report.trend, {
        periodKey: (period) => period.period_start,
        periodEndKey: (period) => period.period_end,
        hasActivity: reconciliationTrendHasActivity,
        showEmptyDays,
        generatedAt: report.generated_at,
      })
    : [];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Reports: Inbound recon history</h1>
          <p style={styles.subtitle}>
            History of inbound Manapool reconciliation runs (daily/weekly drift, recurring mismatches,
            policy outcomes). For live SyncBase vs channel inventory confidence, use Channel Parity.
          </p>
        </div>
        <div style={styles.headerLinks}>
          <Link to="/reports/channel-parity" style={styles.linkButton}>Open Channel Parity</Link>
          <Link to="/reports/pull-list" style={styles.linkButton}>Open Pull List Report</Link>
        </div>
      </div>

      <ReportGeneratedHeader
        generatedAt={report?.generated_at}
        onRefresh={() => {
          void loadReport();
        }}
        refreshing={isLoading}
      />

      <form style={styles.filtersRow} onSubmit={handleApplyFilters}>
        <label style={styles.filterLabel}>
          View
          <select value={view} onChange={(event) => setView(event.target.value as 'daily' | 'weekly')} style={styles.input}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </label>

        <label style={styles.filterLabel}>
          Channel
          <input value={channel} onChange={(event) => setChannel(event.target.value)} style={styles.input} placeholder="manapool" />
        </label>

        <label style={styles.filterLabel}>
          Set
          <input value={setName} onChange={(event) => setSetName(event.target.value)} style={styles.input} placeholder="Duskmourn" />
        </label>

        <label style={styles.filterLabel}>
          Condition
          <input value={condition} onChange={(event) => setCondition(event.target.value)} style={styles.input} placeholder="NM" />
        </label>

        <label style={styles.filterLabel}>
          Language
          <input value={language} onChange={(event) => setLanguage(event.target.value)} style={styles.input} placeholder="EN" />
        </label>

        <label style={styles.filterLabel}>
          Discrepancy Type
          <select value={discrepancyType} onChange={(event) => setDiscrepancyType(event.target.value)} style={styles.input}>
            {discrepancyOptions.map((option) => (
              <option key={option.value || 'all'} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>

        <label style={styles.filterLabel}>
          Top Offenders
          <input type="number" min={1} max={200} value={limit} onChange={(event) => setLimit(event.target.value)} style={styles.input} />
        </label>

        <div style={styles.actions}>
          <button type="submit" style={styles.primaryButton} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Apply'}
          </button>
          <button type="button" style={styles.secondaryButton} onClick={handleExportCsv} disabled={isLoading || isExporting}>
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </form>

      {error && <div style={styles.error}>{error}</div>}

      {report && (
        <>
          <div style={styles.summaryGrid}>
            <MetricCard label="Discrepancies" value={report.summary.total_discrepancies.toString()} detail={`${report.summary.total_runs} runs`} />
            <MetricCard label="Unresolved Variants" value={report.summary.unresolved_variants.toString()} detail="Latest action still detect/suggest" />
            <MetricCard label="Resolved Variants" value={report.summary.resolved_variants.toString()} detail="Latest action applied" />
            <MetricCard label="Window" value={titleCase(report.params.view)} detail={`${report.params.lookback_days}-day lookback`} />
          </div>

          <div style={styles.sectionGrid}>
            <section style={styles.card}>
              <h2 style={styles.sectionTitle}>Discrepancy Breakdown</h2>
              <KeyValueList values={report.summary.discrepancy_counts} emptyLabel="No discrepancies in window" />
            </section>

            <section style={styles.card}>
              <h2 style={styles.sectionTitle}>Policy Outcomes</h2>
              <KeyValueList values={report.summary.policy_outcomes} emptyLabel="No policy outcomes recorded" />
            </section>

            <section style={styles.card}>
              <h2 style={styles.sectionTitle}>Unresolved Age Buckets</h2>
              <ul style={styles.metricList}>
                {report.unresolved_age_buckets.map((bucket) => (
                  <li key={bucket.bucket} style={styles.metricListItem}>
                    <span>{bucket.bucket}</span>
                    <strong>{bucket.count}</strong>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Top Recurring Mismatch SKUs</h2>
              <span style={styles.metaText}>Showing {report.top_recurring_skus.length} rows</span>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Card</th>
                    <th style={styles.th}>Channel</th>
                    <th style={styles.th}>Set</th>
                    <th style={styles.th}>Variant</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Occurrences</th>
                    <th style={styles.th}>Qty Delta</th>
                    <th style={styles.th}>Policy</th>
                    <th style={styles.th}>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {report.top_recurring_skus.length === 0 && (
                    <tr>
                      <td colSpan={9} style={styles.emptyCell}>No recurring mismatches match the current filters.</td>
                    </tr>
                  )}
                  {report.top_recurring_skus.map((item) => (
                    <tr key={`${item.channel}-${item.product_id}-${item.condition}-${item.language}-${item.printing}-${item.discrepancy_type}`}>
                      <td style={styles.tdStrong}>{item.product_name}</td>
                      <td style={styles.td}>{item.channel}</td>
                      <td style={styles.td}>{item.set_name || '-'}</td>
                      <td style={styles.td}>{formatVariant(item.condition, item.language, item.printing, item.foil)}</td>
                      <td style={styles.td}>{formatLabel(item.discrepancy_type)}</td>
                      <td style={styles.td}>{item.occurrences}</td>
                      <td style={styles.td}>{item.quantity_delta}</td>
                      <td style={styles.td}>{item.current_policy_action || '-'}</td>
                      <td style={styles.td}>{formatTimestamp(item.last_seen_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>{titleCase(report.params.view)} Trend</h2>
              <span style={styles.metaText}>Grouped by {report.params.view === 'daily' ? 'day' : 'week'}</span>
              <ShowEmptyDaysToggle checked={showEmptyDays} onChange={setShowEmptyDays} />
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Period Start</th>
                    <th style={styles.th}>Total Discrepancies</th>
                    <th style={styles.th}>Mismatch Types</th>
                    <th style={styles.th}>Policy Outcomes</th>
                    <th style={styles.th}>Channels</th>
                  </tr>
                </thead>
                <tbody>
                  {trendRows.length === 0 && (
                    <tr>
                      <td colSpan={5} style={styles.emptyCell}>
                        {report.trend.length === 0
                          ? 'No trend data is available for the selected filters.'
                          : 'No activity in this window.'}
                      </td>
                    </tr>
                  )}
                  {trendRows.map((period) => (
                    <tr key={period.period_start}>
                      <td style={styles.td}>{formatTimestamp(period.period_start)}</td>
                      <td style={styles.td}>{period.total_discrepancies}</td>
                      <td style={styles.td}>{formatCounts(period.discrepancy_counts)}</td>
                      <td style={styles.td}>{formatCounts(period.policy_outcomes)}</td>
                      <td style={styles.td}>{formatCounts(period.channels)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

function MetricCard({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div style={styles.metricCard}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={styles.metricValue}>{value}</strong>
      <span style={styles.metricDetail}>{detail}</span>
    </div>
  );
}

function KeyValueList({ values, emptyLabel }: { values: Record<string, number>; emptyLabel: string }) {
  const entries = Object.entries(values);
  if (entries.length === 0) {
    return <div style={styles.emptyText}>{emptyLabel}</div>;
  }

  return (
    <ul style={styles.metricList}>
      {entries.map(([key, value]) => (
        <li key={key} style={styles.metricListItem}>
          <span>{formatLabel(key)}</span>
          <strong>{value}</strong>
        </li>
      ))}
    </ul>
  );
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatLabel(value: string): string {
  return value
    .split('_')
    .map((part) => titleCase(part))
    .join(' ');
}

function formatCounts(values: Record<string, number>): string {
  const entries = Object.entries(values);
  if (entries.length === 0) {
    return '-';
  }
  return entries.map(([key, value]) => `${formatLabel(key)}: ${value}`).join(' | ');
}

function formatTimestamp(value: string | null): string {
  return formatInstant(value, '-');
}

function formatVariant(
  condition: string | null,
  language: string | null,
  printing: string | null,
  foil: boolean,
): string {
  const parts = [condition || '-', language || '-', printing || '-', foil ? 'Foil' : 'Non-foil'];
  return parts.join(' / ');
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.lg,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  headerLinks: {
    display: 'flex',
    gap: spacing.sm,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    marginTop: spacing.xs,
    marginBottom: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
    maxWidth: '960px',
  },
  linkButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    textDecoration: 'none',
    color: colors.text,
    backgroundColor: colors.surface,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'end',
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  filterLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    minWidth: '150px',
  },
  input: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.body.fontSize,
    color: colors.text,
    backgroundColor: colors.background,
  },
  actions: {
    display: 'flex',
    gap: spacing.sm,
  },
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  error: {
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: spacing.md,
  },
  metricCard: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  metricLabel: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  metricValue: {
    color: colors.text,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
  },
  metricDetail: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  sectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: spacing.md,
  },
  card: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.sm,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    margin: 0,
    color: colors.text,
    fontSize: typography.h3.fontSize,
    fontWeight: typography.h3.fontWeight,
  },
  metaText: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  metricList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
  metricListItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    color: colors.text,
    fontSize: typography.body.fontSize,
  },
  emptyText: {
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typography.body.fontSize,
    verticalAlign: 'top',
  },
  tdStrong: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    verticalAlign: 'top',
  },
  emptyCell: {
    padding: spacing.lg,
    textAlign: 'center',
    color: colors.textSecondary,
  },
};